export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  return window.self !== window.top;
}

export function isInSameOriginIframe(): boolean {
  if (!isInIframe()) return false;

  try {
    // Accessing parent origin throws if cross-origin
    return Boolean(window.top?.location.href);
  } catch {
    return false;
  }
}

export function isCrossOriginIframe(): boolean {
  return isInIframe() && !isInSameOriginIframe();
}

export function getScrollableParent(element: HTMLElement): HTMLElement | Window {
  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (/(auto|scroll)/.test(overflow + overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

/**
 * Returns the total height of sticky elements visually stacked above the
 * element. Walks up the DOM all the way to `<body>`, checking preceding
 * siblings at each level for `position: sticky`.
 */
export function getStickyOffset(element: HTMLElement): number {
  let offset = 0;
  let current: HTMLElement | null = element;

  while (current && current !== document.body) {
    let sibling = current.previousElementSibling as HTMLElement | null;
    while (sibling) {
      if (window.getComputedStyle(sibling).position === 'sticky') {
        offset += sibling.offsetHeight;
      }
      sibling = sibling.previousElementSibling as HTMLElement | null;
    }
    current = current.parentElement;
  }

  return offset;
}

export function scrollIntoViewIfNeeded(element: HTMLElement | null) {
  if (!element) {
    return;
  }

  requestAnimationFrame(() => {
    const rect = element.getBoundingClientRect();
    const header = document.querySelector('[data-header]') as HTMLElement | null;
    const headerHeight = header?.getBoundingClientRect().height || 0;
    const stickyOffset = getStickyOffset(element);
    const topThreshold = headerHeight + stickyOffset;

    const isInView = rect.top >= topThreshold && rect.bottom <= window.innerHeight;

    if (!isInView) {
      element.style.scrollMarginTop = `${topThreshold}px`;
      element.scrollIntoView({ behavior: 'auto', block: 'start' });
      element.style.scrollMarginTop = '';
    }
  });
}

export function scrollToElementInParent(element: HTMLElement | null, offset = 16) {
  if (!element) return;

  requestAnimationFrame(() => {
    const scrollableParent = getScrollableParent(element);

    if (!(scrollableParent instanceof HTMLElement)) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const elementRect = element.getBoundingClientRect();
    const parentRect = scrollableParent.getBoundingClientRect();
    const elementTop = elementRect.top - parentRect.top + scrollableParent.scrollTop;

    scrollableParent.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth',
    });
  });
}
